import { validateEslMac } from '@/mobile/utils/mobileUtils';
import { ProductOption } from '@/types/pages/device';
import { Category, LayoutDetails, SingleLayoutDetails, TagDevice } from '@/types/pages/storeManagement/device';
import {
    ASSOCIATE_PAIRING_MODE,
    AssociateModeRelations,
    AssociationPayload,
    AssociationState,
    CAMPAIGN_LAYOUT_PURPOSE,
    IdentifierInfo,
    ImageOptions,
    LayoutIdRelations,
    LayoutWithCampaignInfo,
    MultiCategoryRelations,
    MultiTempRelations,
    PAIRING_MODE,
    PlanogramConfig,
    Profile,
} from '@/types/pages/storeManagement/device/associate';
import { PlanogramProductInfo, ProductData } from '@/types/pages/storeManagement/shelfPlanogram';
import { chain, find, groupBy, isEmpty, orderBy } from 'lodash';
import { encodedMACFormat, decodeMac, formatBarcode } from './inputUtils';
import { IDENTIFIER_TYPE } from '@/types/pages/storeManagement/stockControl';
import { StoreSettings } from '@/types';

export const deriveModeConfig = (
    product: ProductData['results'],
    planogramConfig: PlanogramConfig,
    campaignEnable: boolean,
    identifierInfo: IdentifierInfo | null,
) => {
    const nextShelfInfo = deriveShelfConfig(product, planogramConfig);
    const nextImageInfo = deriveImageConfig(product, campaignEnable, identifierInfo);
    return {
        nextShelfInfo,
        nextImageInfo,
    };
};

export const deriveShelfConfig = (
    product: ProductData['results'],
    { noOption, autoSelect, enable }: PlanogramConfig,
) => {
    if (enable) {
        const openAutoSelect = !noOption && autoSelect;
        const { relations, initOptions } = initShelf(product, noOption);
        const options = formateShelf(relations, initOptions, openAutoSelect);

        return {
            openFeature: enable,
            options,
            shouldOpenDialog: options.length > 0,
            relations,
        };
    }
    return {
        openFeature: false,
    };
};

const initShelf = (product: ProductData['results'], noOption: boolean) => {
    const relations: Record<string, string> = {};
    const initOptions: PlanogramProductInfo[] = [];
    product.reduce((result, value, key) => {
        const { http_status, keyword, payload } = value;
        const id = `${keyword}-${key}`;
        relations[id] = '';

        if (http_status !== 200) return result;

        const { product_name, planogram_priority_type, product_slots } = payload;
        const base = (product_slots || []).map((o) => ({
            label: o.shelf_name,
            type: o.shelf_type,
            value: String(o.id),
        }));
        const defaultValue = base.find((o) => o.type === planogram_priority_type)?.value || '';
        relations[id] = defaultValue;

        result.push({
            id,
            name: product_name || 'Empty Product',
            barcode: keyword,
            defaultValue,
            slot: noOption
                ? [
                      ...base,
                      {
                          label: 'No Option',
                          value: '',
                      },
                  ]
                : base,
        });

        return result;
    }, initOptions);
    return { relations, initOptions };
};

const formateShelf = (
    relations: Record<string, string>,
    initOptions: PlanogramProductInfo[],
    openAutoSelect: boolean,
) => {
    if (openAutoSelect) {
        for (const o of initOptions) {
            if (o.slot.length === 1) {
                relations[o.id] = o.slot?.[0].value ?? '';
            }
        }
        return initOptions.filter((o) => o.slot.length > 1);
    }

    return initOptions.filter((o) => o.slot.length > 0);
};

const ensureTrailingSlash = (url: string): string => {
    return url.endsWith('/') ? url : `${url}/`;
};
const ensureProtocol = (url: string): string => {
    const normalizedUrl = url.trim();
    if (normalizedUrl.startsWith('//')) {
        return `${window.location.protocol}${normalizedUrl}`;
    }

    return normalizedUrl;
};

const buildUrl = (base: string, path: string): string => {
    const normalizedBase = ensureTrailingSlash(ensureProtocol(base));
    try {
        const url = new URL(path, normalizedBase).toString();
        return url;
    } catch {
        return '';
    }
};

export const deriveImageConfig = (
    product: ProductData['results'],
    enable: boolean,
    identifierInfo?: IdentifierInfo | null,
) => {
    if (!enable) {
        return {
            openFeature: false,
        };
    }

    const relations: Record<string, string> = {};
    const initOptions: ImageOptions[] = [];

    product.reduce((result, value, key) => {
        const { http_status, keyword, payload } = value;
        const id = `${keyword}-${key}`;
        relations[id] = '';

        if (http_status !== 200 || !payload) return result;

        const { product_name, campaign_assets } = payload;

        const filteredAssets = campaign_assets.filter((item) => {
            if (!identifierInfo) return true;
            return (
                identifierInfo.width === item.img_width &&
                identifierInfo.height === item.img_height &&
                identifierInfo.bpp === item.img_color
            );
        });

        const grouped = groupBy(orderBy(filteredAssets, ['slot_no', 'priority'], ['asc', 'asc']), 'slot_no');

        const slot = Object.values(grouped).map((items) => {
            const item = items[0];

            return {
                id: item.id,
                previewUrl: buildUrl(item.asset_preview_uri, item.asset_original_filename),
                img_height: item.img_height,
                img_width: item.img_width,
                img_name: `${item.target_identifier}-${item.img_name}-${item.slot_no}`,
            };
        });

        result.push({
            id,
            name: product_name,
            barcode: keyword,
            slot,
        });

        return result;
    }, initOptions);

    const options = initOptions.filter((o) => o.slot.length > 0);

    return {
        openFeature: options.length > 0,
        options,
        relations,
    };
};

export const deriveInitStyleInfo = (category: MultiCategoryRelations, profile: Profile) => {
    // Right now only support style which capacity <= 4
    const styleOptions = (category[profile.identifier] || []).filter((o) => o.capacity <= 4);

    if (styleOptions.length > 0) {
        return {
            styleOptions,
            styleInfo: styleOptions[0],
        };
    }
    return { styleOptions, styleInfo: {} as Category };
};

export const findStyleInfo = (id: number, options: Category[]) => {
    return find(options, { id }) || {};
};

export const formatLayoutOptions = (options: SingleLayoutDetails[] | LayoutDetails[]) => {
    if (!options.length) return [];
    return options.map((o) => ({
        order_idx: o.order_idx,
        id: 'layout_id' in o ? o.layout_id : o.sub_layout_id,
        name: 'layout_name' in o ? o.layout_name : o.sub_layout_name,
        previewUrl: o.preview,
        purpose: getLayoutPurpose(o as LayoutWithCampaignInfo),
    }));
};

const getLayoutPurpose = (layout: LayoutWithCampaignInfo) => {
    if (typeof layout.layout_purpose === 'number') return layout.layout_purpose;
    if (typeof layout.sub_layout_purpose === 'number') return layout.sub_layout_purpose;
    return null;
};

const isCampaignLayout = (layout: LayoutWithCampaignInfo) => {
    return getLayoutPurpose(layout) === CAMPAIGN_LAYOUT_PURPOSE;
};

export const splitLayoutsByCampaign = <T extends object>(options: T[]) => {
    const initLayoutInfo = {
        campaign: [] as T[],
        nonCampaign: [] as T[],
    };

    if (!options.length) {
        return initLayoutInfo;
    }

    return options.reduce((acc, layout) => {
        if (isCampaignLayout(layout as LayoutWithCampaignInfo)) {
            acc.campaign.push(layout);
            return acc;
        }

        acc.nonCampaign.push(layout);
        return acc;
    }, initLayoutInfo);
};

export const filterLayoutsByPairingMode = <T extends object>(
    options: T[],
    selectedPairingMode: ASSOCIATE_PAIRING_MODE,
) => {
    const { campaign, nonCampaign } = splitLayoutsByCampaign(options);

    return selectedPairingMode === ASSOCIATE_PAIRING_MODE.NORMAL ? nonCampaign : campaign;
};

export const hasAvailableLayoutsForPairingMode = <T extends object>(
    options: T[],
    selectedPairingMode: ASSOCIATE_PAIRING_MODE,
) => {
    return filterLayoutsByPairingMode(options, selectedPairingMode).length > 0;
};

export const deriveMultiPayloads = ({
    profiles,
    multiAssociateTemp,
    planogramConfig,
    campaignEnable,
    selectedPairingMode,
}: AssociationState) => {
    const payloads = chain(multiAssociateTemp)
        .entries()
        .filter(([key]) => {
            const profile = profiles[key];
            if (!profile || isEmpty(profile.subLayouts)) return false;
            const profileLayouts = Object.values(profile.subLayouts || {}).flat();
            return hasAvailableLayoutsForPairingMode(profileLayouts, selectedPairingMode);
        })
        .map(([key, o]) => {
            const profile = profiles[key];
            const products = (o.products as ProductOption[]).map((o) => o.barcode);
            const basePayload: Partial<AssociationPayload> = {
                macs: profile.mac,
                products: products,
                overwrite_layout: o.layoutId ?? 'default',
                multi_layout_skeleton_id: o.skeletonId,
            };

            if (!isEmpty(o.shelfIdRelations) && planogramConfig.enable) {
                basePayload['overwrite_shelf_codes'] = deriveIds(products, o.shelfIdRelations);
            }
            if (selectedPairingMode === ASSOCIATE_PAIRING_MODE.IMAGE && !isEmpty(o.imageRelations) && campaignEnable) {
                basePayload['overwrite_campaign_id'] = deriveIds(products, o.imageRelations);
            }
            return basePayload;
        })
        .value();

    return payloads;
};

export const deriveIds = (products: string[], relations: AssociateModeRelations) => {
    return products.map((product, index) => Number(relations[`${product}-${index}`]) || null);
};

export const deriveSinglePayloads = (
    {
        singleAssociateTemp,
        profiles,
        planogramConfig,
        campaignEnable,
        selectedPairingMode,
        imageInfo,
    }: AssociationState,
    relations: AssociateModeRelations = {},
) => {
    const payloads = chain(singleAssociateTemp.layoutIdRelations)
        .entries()
        .filter(([key]) => {
            const profile = profiles[key];
            if (!profile) return false;

            const availableLayouts = filterLayoutsByPairingMode(profile.layouts || [], selectedPairingMode);
            if (selectedPairingMode === ASSOCIATE_PAIRING_MODE.IMAGE) {
                return availableLayouts.length > 0 && imageInfo.openFeature;
            }

            return availableLayouts.length > 0;
        })
        .map(([key, o]) => {
            const profile = profiles[key];
            const product = (singleAssociateTemp.product as ProductOption).barcode;
            const basePayload: Partial<AssociationPayload> = {
                macs: profile.mac,
                products: [product],
                overwrite_layout: o ?? 'default',
            };

            if (relations.source === PAIRING_MODE.SHELF && planogramConfig.enable) {
                basePayload['overwrite_shelf_codes'] = [Number(relations[`${product}-0`]) || null];
            }
            if (
                selectedPairingMode === ASSOCIATE_PAIRING_MODE.IMAGE &&
                relations.source === PAIRING_MODE.IMAGE &&
                campaignEnable
            ) {
                basePayload['overwrite_campaign_id'] = [Number(relations[`${product}-0`]) || null];
            }
            return basePayload;
        })
        .value();

    return payloads;
};

export const deriveInitInfo = (device: TagDevice[]) => {
    const multiAssociateTemp: MultiTempRelations = {};
    const layoutIdRelations: LayoutIdRelations = {};
    const profiles = chain(device)
        .filter((o) => !!o.profile_id && o.lp_identifier !== 'unknown')
        .groupBy('lp_identifier')
        .entries()
        .map(([key, device]) => {
            const { lp_identifier, lp_name, single_layouts, multi_layouts } = device[0] || {};
            const hasSingleLayouts = !isEmpty(single_layouts);
            const hasMultiLayouts = !isEmpty(multi_layouts);

            if (hasSingleLayouts) {
                layoutIdRelations[lp_identifier] = null;
            }

            if (hasMultiLayouts) {
                multiAssociateTemp[lp_identifier] = {
                    isFinish: false,
                    products: [],
                    layoutId: null,
                    skeletonId: null,
                    shelfIdRelations: {},
                    imageRelations: {},
                };
            }

            return [
                key,
                {
                    mac: device.map((o) => o.mac),
                    identifier: lp_identifier,
                    name: lp_name,
                    layouts: hasSingleLayouts ? single_layouts : [],
                    subLayouts: hasMultiLayouts ? multi_layouts : {},
                },
            ];
        })
        .fromPairs()
        .value();
    return { multiAssociateTemp, layoutIdRelations, profiles };
};

export const parseRelationId = (id: string) => {
    return Number(id) || null;
};

export const filterCategoryByCapacity = (category: MultiCategoryRelations, capacity: number = 4): Category[] => {
    if (!category) return [];
    // 直接遍歷所有 styleInfo 陣列，避免多餘的 flatten 運算
    const result: Category[] = [];
    for (const styleInfo of Object.values(category)) {
        for (const item of styleInfo) {
            if (item.capacity <= capacity) {
                result.push(item);
            }
        }
    }
    return result;
};

const detectInputType = (value: string) => {
    return validateEslMac(value) ? IDENTIFIER_TYPE.MAC : IDENTIFIER_TYPE.BARCODE;
};

export const normalizeIdentifier = (value: string, settings: Partial<StoreSettings>) => {
    const type = detectInputType(value);

    let normalized = '';
    if (type === IDENTIFIER_TYPE.MAC) {
        normalized = encodedMACFormat(value) ? decodeMac(value.substring(2)) : value;
    } else {
        normalized = formatBarcode(settings.auto_convert_upce_to_upca === '1', settings.addLeadingZero === '1', value);
    }
    return { type, normalized };
};

export const parseLpIdentifierInfo = (identifier: string): IdentifierInfo | null => {
    const parts = identifier.split('-');
    /**
     * medium-22-3-colors-bwr-1-250x122
     * {profile-size}-{sizeCode}-{colorNum}-colors-{colorCode}-{Bpp}-{width}x{height}
     **/
    if (parts.length <= 6) {
        return null;
    }

    // ---- bpp ----
    const bppPart = parts[5];
    const bpp = Number.isNaN(Number(bppPart)) ? -1 : Number(bppPart);

    // ---- width / height ----
    const sizePart = parts[6];
    const sizeMatch = sizePart.match(/^(\d+)x(\d+)$/);

    const width = sizeMatch ? Number(sizeMatch[1]) : 0;
    const height = sizeMatch ? Number(sizeMatch[2]) : 0;

    return {
        width,
        height,
        bpp,
    };
};
